import { Children, Fragment, HTMLAttributes, memo, useCallback, useMemo } from 'react'

import { Remove } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { AvcOutputConfig, AvcSdiOutputConfig } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { omit } from 'lodash-es'
import { useForm, Controller, useFormContext, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InlineConfirm } from '@shared/components/Confirm'
import { InputErrorEndAdornment } from '@shared/components/ErrorHandling'
import { ListBoxInfinite } from '@shared/components/MaterialUIEnhancements/AutoComplete'
import { DialogFormContent } from '@shared/layouts'
import {
  FormStructure as AvcTaskFormStructure,
  Metadata,
  useMeaningfulDefaultName,
} from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Task/Config'
import { Item as VariantItem } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Task/Config/Variants'
import { useNanoID } from 'hooks/utils'
import { AdvancedSettings } from 'layouts'
import { avcInstanceQueryKeys } from 'services/queryKeys'
import { stopPropagate } from 'utils'

type FormStructure = AvcSdiOutputConfig &
  Pick<AvcOutputConfig, 'metadata'> & {
    enabled?: boolean
    name?: string | null
  }

type SdiProps = {
  onSubmit: (data: AvcOutputConfig) => void
  outputIdx?: number
}

const SdiRaw = ({ onSubmit, outputIdx }: SdiProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])

  const generatedId = useNanoID()

  const { getValues, watch: watchAvcTaskForm } = useFormContext<AvcTaskFormStructure>()

  const avcInstancesBatchQuery = useQuery({
    ...avcInstanceQueryKeys.batchGet({
      avcInstanceBatchGet: { ids: getValues().config!.executor.instanceIds! },
    }),
    enabled: !!getValues().config?.executor.instanceIds?.length,
  })

  const selectedOutput = useMemo(
    () => (outputIdx !== undefined ? getValues().config?.data.outputs?.at(outputIdx) : undefined),
    [getValues, outputIdx],
  )

  const meaningFulDefaultName = useMeaningfulDefaultName('outputs')

  const { control, handleSubmit, formState } = useForm<FormStructure>({
    defaultValues: {
      name: selectedOutput?.name,
      enabled: !selectedOutput?.disabled,
      ...selectedOutput?.sdi,
      metadata: selectedOutput?.metadata,
    },
  })

  const metadataFieldArray = useFieldArray({ control, name: 'metadata' })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      const transformedData: AvcOutputConfig = {
        sdi: omit(data, ['name', 'enabled']),
        type: 'Sdi',
        id: selectedOutput?.id || generatedId,
        name: data.name || meaningFulDefaultName,
        disabled: !data.enabled,
        metadata: data.metadata,
      }

      onSubmit(transformedData)
    },
    [generatedId, selectedOutput?.id, meaningFulDefaultName, onSubmit],
  )

  const options =
    avcInstancesBatchQuery.data?.items.flatMap(
      (avcInstance) =>
        avcInstance.capabilities.avio.sdi?.map((sdiInstance) => ({
          id: sdiInstance.id,
          name: sdiInstance.name,
          avcInstanceName: avcInstance.name,
          avcInstanceId: avcInstance.id,
        })) ?? [],
    ) ?? []

  return (
    <DialogFormContent
      title={t('domain:Workspace.AvcTask.manageSdiOutput')}
      onSubmit={stopPropagate(handleSubmit(handleFormSubmit))}
      Illustration={null}
      SaveButtonProps={{
        disabled: formState.isSubmitted && !formState.isValid,
      }}
    >
      <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'auto', gap: 2 }}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState, formState }) => (
            <TextField
              sx={{ gridColumn: '1 / span 2' }}
              {...field}
              label={t('name')}
              placeholder={meaningFulDefaultName}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />,
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              control={<Switch {...field} checked={field.value} onChange={(_, checked) => field.onChange(checked)} />}
              label={t('domain:Workspace.AvcTask.enabled')}
              labelPlacement="start"
            />
          )}
        />
        <Controller
          control={control}
          name="variantId"
          rules={{
            required: {
              value: true,
              message: t('domain:Workspace.AvcTask.selectOneVariant'),
            },
          }}
          render={({ field, fieldState }) => (
            <FormControl sx={{ gridColumn: '1 / -1' }}>
              <InputLabel id="variantID">{t('entity:content.AvcTask.variant', { count: 1 })}</InputLabel>
              <Select
                {...field}
                labelId="variantID"
                input={
                  <OutlinedInput
                    label={t('entity:content.AvcTask.variant', { count: 1 })}
                    endAdornment={
                      <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                    }
                  />
                }
                renderValue={(selected) => {
                  const variant = watchAvcTaskForm('config.data.variants')?.find((item) => item.id === selected)

                  if (variant) {
                    return (
                      <VariantItem
                        sx={[{ my: -1 }, !!variant.disabled && { color: 'action.disabled' }]}
                        variant={variant}
                      />
                    )
                  }
                }}
              >
                {watchAvcTaskForm('config.data.variants')?.map((variant) => (
                  <MenuItem
                    sx={[!!variant.disabled && { color: 'action.disabled' }]}
                    key={variant.id}
                    value={variant.id}
                  >
                    <VariantItem variant={variant} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="device"
          rules={{
            required: {
              value: true,
              message: t('domain:Workspace.AvcTask.selectOneSdiDevice'),
            },
          }}
          render={({ field, fieldState }) => (
            <Autocomplete
              sx={{ gridColumn: '1 / span 3' }}
              {...field}
              value={options.find((option) => field.value === option.id) || null}
              options={options}
              getOptionLabel={(option) => option.name}
              groupBy={(option) => option.avcInstanceName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                field.onChange(value?.id)
              }}
              renderGroup={(params) => [
                <Typography sx={{ ml: 1, mb: 1, mt: 2 }} key={params.key} component="li" variant="heading-base">
                  {params.group}
                </Typography>,
                ...Children.toArray(params.children),
              ]}
              renderOption={(props, option, { selected }) => (
                <ListItemButton
                  sx={{ mx: 1, my: 0.5, borderRadius: 1 }}
                  {...(props as HTMLAttributes<HTMLElement>)}
                  key={`${option.avcInstanceId}-${option.id}`}
                  selected={selected}
                >
                  <ListItemIcon>
                    <Typography component="span" variant="text-sm">
                      {t('domain:Workspace.AvcTask.sdi')}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText>{option.name}</ListItemText>
                </ListItemButton>
              )}
              fullWidth
              ListboxComponent={ListBoxInfinite}
              renderTags={(value) => {
                const numTags = value.length

                return (
                  <Typography sx={{ ml: 1.5 }} variant="text" noWrap>
                    {numTags} {t('domain:Workspace.AvcTask.SdiSelected')}
                  </Typography>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('domain:Workspace.AvcTask.selectPhysicalSdiOutput')}
                  error={!!fieldState.error}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={t('domain:Workspace.AvcTask.sdiOutputError')}
            />
          )}
        />

        <AdvancedSettings
          sx={{
            gridColumn: '1 / span 3',
          }}
        >
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'auto', gap: 2 }}>
            <Controller
              control={control}
              name="preroll"
              render={({ field, fieldState }) => (
                <TextField
                  sx={{ flex: 1 }}
                  {...field}
                  type="number"
                  label={t('domain:Workspace.AvcTask.preroll')}
                  InputProps={{
                    endAdornment: (
                      <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                    ),
                  }}
                />
              )}
            />
          </Box>
        </AdvancedSettings>

        <Metadata
          sx={{ gridColumn: '1 / span 3' }}
          hasItems={!!metadataFieldArray.fields.length}
          onPrepend={() => metadataFieldArray.prepend({})}
        >
          {metadataFieldArray.fields.map((item, fieldIdx) => (
            <Fragment key={item.id}>
              <Controller
                control={control}
                name={`metadata.${fieldIdx}.key`}
                render={({ field, fieldState, formState }) => (
                  <TextField
                    {...field}
                    size="small"
                    label={t('domain:Workspace.AvcTask.key')}
                    placeholder={t('domain:Workspace.AvcTask.egContent')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name={`metadata.${fieldIdx}.value`}
                render={({ field, fieldState, formState }) => (
                  <TextField
                    {...field}
                    multiline
                    minRows={1}
                    maxRows={4}
                    size="small"
                    label={t('domain:Workspace.AvcTask.value')}
                    placeholder={t('domain:Workspace.AvcTask.jsonValueExample')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                      ),
                    }}
                  />
                )}
              />
              <InlineConfirm
                question={t('areYouSure')}
                onCheck={(closeConfirm) => {
                  closeConfirm()
                  setTimeout(() => {
                    metadataFieldArray.remove(fieldIdx)
                  }, 300)
                }}
              >
                {({ openConfirm }) => (
                  <IconButton sx={{ alignSelf: 'center' }} size="medium" onClick={(e) => openConfirm(e.currentTarget)}>
                    <Remove fontSize="small" />
                  </IconButton>
                )}
              </InlineConfirm>
            </Fragment>
          ))}
        </Metadata>
      </Box>
    </DialogFormContent>
  )
}

export const Sdi = memo(SdiRaw)
